import React, { FunctionComponent, MouseEvent } from 'react';
import { useBreakpoint } from '@noths/polaris-client-responsive';
import { Breakpoint } from '@noths/polaris-client-styles';
import { toSentenceCase } from '@noths/polaris-client-utils';

import arrowBack from 'src/assets/svg/arrowBack.svg';
import { Filter, FilterOptionValue, TreeFilterOption } from 'src/types';
import { FilterOptionsProps } from '../../types';
import { FilterOptionsExpandableContent } from './FilterOptionsExpandableContent';
import * as styles from '../../styles/Tree.styles';

export type TreeProps = Omit<FilterOptionsProps, 'currencyCode'> & {
  pathWithoutCurrentFilter: string;
  parentNodes: TreeFilterOption[];
  childNodes: TreeFilterOption[];
};

export const onClick = (
  event: MouseEvent<HTMLAnchorElement>,
  value: FilterOptionValue,
  depth: number,
  filter: Filter,
  onSelectFilterOption: FilterOptionsProps['onSelectFilterOption'],
) => {
  event.preventDefault();

  onSelectFilterOption({
    filterUid: filter.uid,
    filterOptionValue: value,
    filterOptionDepth: depth,
  });
};

export const Tree: FunctionComponent<TreeProps> = ({
  filter,
  onSelectFilterOption,
  pathWithoutCurrentFilter,
  parentNodes,
  childNodes,
  onExpandableContentToggle,
}: TreeProps) => {
  const totalBranches = parentNodes.length + childNodes.length;
  const isOnSmallScreen = useBreakpoint() === Breakpoint.SMALL;
  const isExpandable = !isOnSmallScreen && totalBranches > 6;
  const isTreeRoot = parentNodes.every((x) => x.depth === 1);

  return (
    <FilterOptionsExpandableContent
      filter={filter}
      onExpandableContentToggle={onExpandableContentToggle}
      isExpandable={isExpandable}
    >
      <ul css={styles.list}>
        {parentNodes.map(({ active, value, title, depth }) => (
          <li key={value}>
            <a
              href={`${pathWithoutCurrentFilter}&${filter.uid}=${value}`}
              css={styles.parentNodeLink}
              onClick={(e) => onClick(e, value, depth, filter, onSelectFilterOption)}
              data-is-active={active}
              aria-label={`${title}${active ? ' (currently selected)' : ''}`}
            >
              {!active && !isOnSmallScreen && !isTreeRoot && (
                <img src={arrowBack} alt="" width={16} height={16} />
              )}
              {toSentenceCase(title)}
            </a>
            {active && childNodes.length > 0 && (
              <ul aria-label={`${toSentenceCase(title)} sub-categories`} css={styles.list}>
                {childNodes.map(({ value, depth, title }) => (
                  <li key={value}>
                    <a
                      href={`${pathWithoutCurrentFilter}&${filter.uid}=${value}`}
                      css={styles.childNodeLink}
                      onClick={(e) => onClick(e, value, depth, filter, onSelectFilterOption)}
                    >
                      {toSentenceCase(title)}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </FilterOptionsExpandableContent>
  );
};
